import { ChevronLeft, ChevronRight, Download, Images } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import { GalleryDownloadConfirmModal } from '../components/GalleryDownloadConfirmModal';
import { fetchAllGalleryListPage } from '../api/galleries';
import { PageSeo } from '../components/PageSeo';
import { SubpageLayout } from '../components/SubpageLayout';
import { useAuth } from '../context/AuthContext';
import { GALLERIES } from '../data/galleries';
import { useGalleryDownload } from '../hooks/useGalleryDownload';

const SKELETON_CARD_COUNT = 4;
const GALLERY_PAGE_SIZE = 10;
const PAGE_QUERY_KEY = 'page';

/**
 * Üyelere özel galeri listesi — URL: /member-area/galleries
 *
 * Canlı veri: `GET /galleries/api/AllGallery/GetAll` (Bearer), sayfalı.
 * Simülasyon veya hata: örnek `GALLERIES`.
 */
export function GalleriesPage() {
    const { t } = useTranslation();
    const { authToken, isSimulatedAuth } = useAuth();
    const useLiveApi = Boolean(authToken && !isSimulatedAuth);
    const [searchParams, setSearchParams] = useSearchParams();

    const [items, setItems] = useState(() => GALLERIES);
    const [meta, setMeta] = useState(
        /** @type {null | { totalCount: number, totalPages: number, currentPage: number, pageSize: number, hasNext: boolean, hasPrevious: boolean }} */ (
            null
        ),
    );
    const [loadError, setLoadError] = useState(
        /** @type {string | null} */ (null),
    );
    const [loading, setLoading] = useState(false);
    const {
        downloadModal,
        closeDownloadModal,
        openGalleryDownload,
        handleDownloadConfirm,
    } = useGalleryDownload({ authToken, useLiveApi });

    const pageFromUrl = Math.max(
        1,
        parseInt(searchParams.get(PAGE_QUERY_KEY) || '1', 10) || 1,
    );

    const goToPage = useCallback(
        (nextPage) => {
            const p = Math.max(1, nextPage);
            if (p <= 1) {
                setSearchParams({}, { replace: true });
            } else {
                setSearchParams({ [PAGE_QUERY_KEY]: String(p) }, { replace: true });
            }
        },
        [setSearchParams],
    );

    useEffect(() => {
        if (!useLiveApi) {
            const source = GALLERIES;
            const totalPages = Math.max(
                1,
                Math.ceil(source.length / GALLERY_PAGE_SIZE),
            );
            const safePage = Math.min(pageFromUrl, totalPages);
            const start = (safePage - 1) * GALLERY_PAGE_SIZE;
            setItems(source.slice(start, start + GALLERY_PAGE_SIZE));
            setMeta({
                totalCount: source.length,
                totalPages,
                currentPage: safePage,
                pageSize: GALLERY_PAGE_SIZE,
                hasNext: safePage < totalPages,
                hasPrevious: safePage > 1,
            });
            setLoadError(null);
            setLoading(false);
            return undefined;
        }

        let cancelled = false;
        setLoading(true);
        setLoadError(null);

        (async () => {
            try {
                const { items: nextItems, meta: nextMeta } =
                    await fetchAllGalleryListPage(authToken, {
                        pageNumber: pageFromUrl,
                        pageSize: GALLERY_PAGE_SIZE,
                    });
                if (cancelled) return;
                setItems(nextItems);
                setMeta(nextMeta);
                setLoadError(null);
            } catch (err) {
                if (cancelled) return;
                const totalPages = Math.max(
                    1,
                    Math.ceil(GALLERIES.length / GALLERY_PAGE_SIZE),
                );
                const safePage = Math.min(pageFromUrl, totalPages);
                const start = (safePage - 1) * GALLERY_PAGE_SIZE;
                setItems(GALLERIES.slice(start, start + GALLERY_PAGE_SIZE));
                setMeta({
                    totalCount: GALLERIES.length,
                    totalPages,
                    currentPage: safePage,
                    pageSize: GALLERY_PAGE_SIZE,
                    hasNext: safePage < totalPages,
                    hasPrevious: safePage > 1,
                });
                setLoadError(
                    err instanceof Error
                        ? err.message
                        : t('galleriesPage.genericLoadError'),
                );
                if (safePage !== pageFromUrl) {
                    goToPage(safePage);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [useLiveApi, authToken, pageFromUrl, t, goToPage]);

    useEffect(() => {
        if (meta && pageFromUrl > meta.totalPages && meta.totalPages >= 1) {
            goToPage(meta.totalPages);
        }
    }, [meta, pageFromUrl, goToPage]);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [pageFromUrl]);

    const showEmpty = useLiveApi && !loadError && !loading && items.length === 0;
    const showPagination =
        meta != null && meta.totalPages > 1 && items.length > 0 && !loading;

    return (
        <>
            <PageSeo
                title={`${t('galleriesPage.title')} — ${t('header.rotaryAlt')}`}
                description={t('seo.galleries')}
                noindex
            />
            <SubpageLayout
                title={t('galleriesPage.title')}
                subtitle={t('galleriesPage.subtitle')}
                contentClassName="galleries-page"
            >
            {loadError ? (
                <p
                    className="text-center small text-secondary mb-3"
                    role="status"
                >
                    {t('galleriesPage.loadErrorNotice', {
                        error: loadError,
                    })}
                </p>
            ) : null}
            {loading ? (
                <div
                    role="status"
                    aria-live="polite"
                    aria-busy="true"
                    className="mb-0"
                >
                    <span className="visually-hidden">
                        {t('galleriesPage.loading')}
                    </span>
                    <SkeletonTheme
                        baseColor="#e8ecf2"
                        highlightColor="#f0f3f8"
                    >
                        <ul className="galleries-grid galleries-grid--skeleton list-unstyled mb-0">
                            {Array.from(
                                { length: SKELETON_CARD_COUNT },
                                (_, i) => (
                                    <li
                                        key={`sk-${i}`}
                                        className="galleries-grid-item"
                                    >
                                        <article className="gallery-card">
                                            <div className="gallery-card-image-wrap">
                                                <Skeleton />
                                            </div>
                                            <div
                                                className="gallery-card-rule"
                                                aria-hidden
                                            />
                                            <div className="gallery-card-body">
                                                <Skeleton
                                                    count={2}
                                                    height={14}
                                                    style={{
                                                        marginBottom: 8,
                                                    }}
                                                />
                                                <Skeleton
                                                    width="42%"
                                                    height={11}
                                                />
                                            </div>
                                        </article>
                                    </li>
                                ),
                            )}
                        </ul>
                    </SkeletonTheme>
                </div>
            ) : showEmpty ? (
                <p className="mb-0 text-secondary">
                    {t('galleriesPage.emptyList')}
                </p>
            ) : (
                <>
                    {meta && useLiveApi ? (
                        <p className="galleries-pagination-summary text-secondary small mb-3 text-start">
                            {t('galleriesPage.paginationSummary', {
                                count: meta.totalCount,
                            })}
                        </p>
                    ) : null}
                    <ul className="galleries-grid list-unstyled mb-0">
                        {items.map((g) => (
                            <li key={g.id} className="galleries-grid-item">
                                <article className="gallery-card">
                                    <Link
                                        to={`/member-area/galleries/${encodeURIComponent(g.slug)}`}
                                        className="gallery-card-link"
                                    >
                                        <div className="gallery-card-image-wrap">
                                            <img
                                                src={g.coverImageSrc}
                                                alt=""
                                                className="gallery-card-image"
                                                width={800}
                                                height={500}
                                                loading="lazy"
                                                decoding="async"
                                            />
                                        </div>
                                        <div
                                            className="gallery-card-rule"
                                            aria-hidden
                                        />
                                    </Link>
                                    <div className="gallery-card-body">
                                        <Link
                                            to={`/member-area/galleries/${encodeURIComponent(g.slug)}`}
                                            className="gallery-card-title-link text-decoration-none"
                                        >
                                            <h2 className="gallery-card-title">
                                                {g.title}
                                            </h2>
                                        </Link>
                                        <p className="gallery-card-meta">
                                            <Images
                                                size={15}
                                                strokeWidth={1.85}
                                                className="gallery-card-meta-icon flex-shrink-0"
                                                aria-hidden
                                            />
                                            <span>
                                                {t(
                                                    'galleriesPage.photoCount',
                                                    {
                                                        count: g.photoCount,
                                                    },
                                                )}
                                            </span>
                                        </p>
                                        <button
                                            type="button"
                                            className="gallery-card-download-btn btn btn-sm d-inline-flex align-items-center gap-1"
                                            onClick={() =>
                                                openGalleryDownload(g)
                                            }
                                            disabled={Boolean(downloadModal)}
                                        >
                                            <Download
                                                size={15}
                                                strokeWidth={2}
                                                aria-hidden
                                            />
                                            {t('galleriesPage.downloadButton')}
                                        </button>
                                    </div>
                                </article>
                            </li>
                        ))}
                    </ul>
                    {showPagination ? (
                        <nav
                            className="galleries-pagination mt-4"
                            aria-label={t('galleriesPage.paginationNavAria')}
                        >
                            <ul className="news-list-pagination-pages list-unstyled mb-0 d-flex flex-wrap justify-content-center align-items-center gap-2">
                                <li>
                                    <button
                                        type="button"
                                        className="news-list-pagination-btn news-list-pagination-btn--nav"
                                        disabled={!meta.hasPrevious}
                                        onClick={() =>
                                            goToPage(meta.currentPage - 1)
                                        }
                                        aria-label={t(
                                            'galleriesPage.paginationPrevAria',
                                        )}
                                    >
                                        <ChevronLeft
                                            size={22}
                                            strokeWidth={2}
                                            aria-hidden
                                        />
                                    </button>
                                </li>
                                {meta.totalPages <= 12
                                    ? Array.from(
                                          { length: meta.totalPages },
                                          (_, i) => i + 1,
                                      ).map((n) => (
                                          <li key={n}>
                                              <button
                                                  type="button"
                                                  className={`news-list-pagination-btn${n === meta.currentPage ? ' is-active' : ''}`}
                                                  onClick={() => goToPage(n)}
                                                  aria-label={t(
                                                      'galleriesPage.paginationPageAria',
                                                      { page: n },
                                                  )}
                                                  aria-current={
                                                      n === meta.currentPage
                                                          ? 'page'
                                                          : undefined
                                                  }
                                              >
                                                  {n}
                                              </button>
                                          </li>
                                      ))
                                    : null}
                                {meta.totalPages > 12 ? (
                                    <li>
                                        <span
                                            className="news-list-pagination-indicator"
                                            aria-current="page"
                                        >
                                            {meta.currentPage} /{' '}
                                            {meta.totalPages}
                                        </span>
                                    </li>
                                ) : null}
                                <li>
                                    <button
                                        type="button"
                                        className="news-list-pagination-btn news-list-pagination-btn--nav"
                                        disabled={!meta.hasNext}
                                        onClick={() =>
                                            goToPage(meta.currentPage + 1)
                                        }
                                        aria-label={t(
                                            'galleriesPage.paginationNextAria',
                                        )}
                                    >
                                        <ChevronRight
                                            size={22}
                                            strokeWidth={2}
                                            aria-hidden
                                        />
                                    </button>
                                </li>
                            </ul>
                        </nav>
                    ) : null}
                </>
            )}
            <GalleryDownloadConfirmModal
                open={Boolean(downloadModal)}
                galleryTitle={downloadModal?.gallery.title || ''}
                phase={downloadModal?.phase || 'loading'}
                downloadStep={downloadModal?.downloadStep || 'fetching'}
                fileCount={downloadModal?.fileCount || 0}
                totalBytes={downloadModal?.totalBytes || 0}
                error={downloadModal?.error}
                onClose={closeDownloadModal}
                onConfirm={handleDownloadConfirm}
            />
        </SubpageLayout>
        </>
    );
}
